/**
 * Landing v2 — shared Contact form section.
 *
 * Used across all 4 marketing pages (home / student / parent / school).
 * Pre-fills `role` based on the source page. POSTs to `/api/contact` and
 * stores submission in `contact_submissions` + emails platform admin.
 *
 * Honeypot field `hp_field` hidden via CSS — bots filling it get a fake
 * success, the request is dropped on the backend.
 */
import { useState } from 'react';
import api from '@/lib/api';
import { toast } from 'sonner';

const ROLES = ['Student', 'Parent', 'School Representative', 'Other'];

const PAGE_TO_DEFAULT_ROLE = {
  home: 'Student',
  student: 'Student',
  parent: 'Parent',
  school: 'School Representative',
};

export default function ContactForm({ sourcePage = 'home', title = 'Contact Us', subtitle }) {
  const defaultRole = PAGE_TO_DEFAULT_ROLE[sourcePage] || 'Student';
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [role, setRole] = useState(defaultRole);
  const [message, setMessage] = useState('');
  const [hpField, setHpField] = useState(''); // honeypot
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (submitting) return;
    if (!name.trim() || !email.trim() || !message.trim()) {
      toast.error('Please fill all required fields.');
      return;
    }
    setSubmitting(true);
    try {
      await api.post('/contact', {
        name: name.trim(),
        email: email.trim(),
        role,
        message: message.trim(),
        hp_field: hpField,
        source_page: sourcePage,
      });
      setSubmitted(true);
      setName(''); setEmail(''); setMessage(''); setRole(defaultRole);
      toast.success('Thanks — we\'ll get back to you shortly.');
    } catch (err) {
      const detail = err?.response?.data?.detail || 'Could not send message. Please try again.';
      toast.error(detail);
    } finally {
      setSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <div className="contact-success-state" data-testid="contact-success">
        <div className="success-icon">
          <svg xmlns="http://www.w3.org/2000/svg" width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
            <polyline points="20 6 9 17 4 12" />
          </svg>
        </div>
        <h3>Message received</h3>
        <p>We&apos;ve got your note and will reply within 1 business day.</p>
        <button
          type="button"
          className="btn btn-text"
          onClick={() => setSubmitted(false)}
          data-testid="contact-submit-another"
        >
          Send another message
        </button>
      </div>
    );
  }

  return (
    <form
      className="signup-form"
      onSubmit={handleSubmit}
      data-testid="contact-form"
      noValidate
    >
      {/* Honeypot — hidden, bots will fill it */}
      <div
        aria-hidden="true"
        style={{ position: 'absolute', left: '-9999px', width: 1, height: 1, overflow: 'hidden' }}
      >
        <label>
          Leave this field empty
          <input
            type="text"
            tabIndex={-1}
            autoComplete="off"
            value={hpField}
            onChange={(e) => setHpField(e.target.value)}
          />
        </label>
      </div>

      <div className="form-group">
        <label htmlFor="contact-name">Full Name</label>
        <input
          type="text" id="contact-name" className="sim-input"
          placeholder="Your name"
          value={name}
          onChange={(e) => setName(e.target.value)}
          required maxLength={120}
          data-testid="contact-name"
        />
      </div>

      <div className="form-group">
        <label htmlFor="contact-email">Email Address</label>
        <input
          type="email" id="contact-email" className="sim-input"
          placeholder="you@example.com"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          required
          data-testid="contact-email"
        />
      </div>

      <div className="form-group">
        <label htmlFor="contact-role">I am a&hellip;</label>
        <select
          id="contact-role" className="sim-select"
          value={role}
          onChange={(e) => setRole(e.target.value)}
          data-testid="contact-role"
        >
          {ROLES.map((r) => <option key={r} value={r}>{r}</option>)}
        </select>
      </div>

      <div className="form-group">
        <label htmlFor="contact-message">Message</label>
        <textarea
          id="contact-message" className="sim-textarea" rows={3}
          placeholder="How can we help you?"
          value={message}
          onChange={(e) => setMessage(e.target.value)}
          required maxLength={4000}
          data-testid="contact-message"
        />
      </div>

      <button
        type="submit"
        className="btn btn-primary btn-block glow-btn-strong"
        disabled={submitting}
        data-testid="contact-submit"
      >
        {submitting ? 'Sending…' : 'Send Message'}
      </button>
    </form>
  );
}
